import React, { useState } from 'react';
import { Book, Shield, Code, Zap, Lock, Users, Github, Copy, Check, ChevronDown, ChevronUp, ExternalLink, Twitter, MessageCircle } from 'lucide-react';
import { CONFIG } from '../config/constants';

export const DocsView = () => {
  const [copiedAddress, setCopiedAddress] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedAddress(id);
    setTimeout(() => setCopiedAddress(null), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const contracts = [
    { name: 'DunkPoge NFT', address: CONFIG.ADDRESSES.NFT, network: 'Base Sepolia', explorer: `https://sepolia.basescan.org/address/${CONFIG.ADDRESSES.NFT}` },
    { name: 'Pogecoin (POGE)', address: CONFIG.ADDRESSES.COIN, network: 'Base Sepolia', explorer: `https://sepolia.basescan.org/address/${CONFIG.ADDRESSES.COIN}` },
    { name: 'DunkPogeStaking', address: CONFIG.ADDRESSES.STAKING, network: 'Base Sepolia', explorer: `https://sepolia.basescan.org/address/${CONFIG.ADDRESSES.STAKING}` }
  ];

  return (
    <div className="space-y-12">
      {/* Hero */}
      <div className="text-center">
        <div className="inline-block bg-gradient-to-r from-purple-600 to-blue-600 text-white px-8 py-4 border-4 border-black shadow-[8px_8px_0_0_#000] mb-6">
          <h1 className="text-4xl md:text-6xl font-black uppercase font-mono">
            DOCUMENTATION
          </h1>
        </div>
        <div className="bg-white text-black p-6 border-4 border-black shadow-[8px_8px_0_0_#000] max-w-3xl mx-auto">
          <p className="text-2xl font-black uppercase mb-3 font-mono">
            Trustless NFT Ecosystem
          </p>
          <p className="text-lg font-mono mb-4">
            10,000 fully on-chain generative NFTs with staking rewards
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <a 
              href="/Dunk_Poge_Documentation.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-600 text-white font-black px-6 py-3 border-4 border-black hover:bg-blue-700 transition-all flex items-center gap-2 no-underline"
            >
              📄 DOWNLOAD PDF
              <ExternalLink size={16} />
            </a>
            <a 
              href="https://github.com/dunkpoge"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white font-black px-6 py-3 border-4 border-black hover:bg-gray-900 transition-all flex items-center gap-2 no-underline"
            >
              <Github size={20} />
              VIEW SOURCE
            </a>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid md:grid-cols-3 gap-4">
        {[
          { label: 'Philosophy', icon: Shield, color: 'bg-purple-600', section: 'philosophy' },
          { label: 'Architecture', icon: Code, color: 'bg-blue-600', section: 'architecture' },
          { label: 'Security', icon: Lock, color: 'bg-green-600', section: 'security' }
        ].map((link) => {
          const Icon = link.icon;
          return (
            <button
              key={link.section}
              onClick={() => document.getElementById(link.section)?.scrollIntoView({ behavior: 'smooth' })}
              className={`${link.color} text-white p-6 border-4 border-white font-black uppercase font-mono shadow-[6px_6px_0_0_#fff] hover:-translate-y-1 hover:shadow-[8px_8px_0_0_#fff] transition-all flex items-center justify-center gap-3`}
            >
              <Icon size={24} />
              {link.label}
            </button>
          );
        })}
      </div>

      {/* Philosophy Section */}
      <div id="philosophy" className="bg-purple-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <div className="flex items-center gap-3 mb-6">
          <Shield size={32} />
          <h2 className="text-3xl font-black uppercase font-mono">Philosophy & Principles</h2>
        </div>
        
        <div className="space-y-6 text-lg font-mono">
          <div className="bg-black/30 p-6 border-2 border-white/50">
            <h3 className="font-black text-xl mb-3 text-yellow-300">ALIGNMENT WITH THE TRUSTLESS MANIFESTO</h3>
            <p className="mb-4">
              Dunk Poge embodies the principles outlined in{' '}
              <a 
                href="https://trustlessness.eth.limo/general/2025/11/11/the-trustless-manifesto.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-green-400 underline hover:text-green-300"
              >
                The Trustless Manifesto
              </a>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'Self-Sovereignty', desc: 'Every user authorizes their own actions. No one mints, stakes, or claims on your behalf.' },
              { title: 'Verifiability', desc: 'All NFT metadata lives on-chain as SVG. Anyone can verify what happened from public data.' },
              { title: 'Censorship Resistance', desc: 'No admin can prevent unstaking or claiming rewards.' },
              { title: 'Walkaway Test', desc: 'If the team disappears, contracts continue functioning. NFTs remain yours.' },
              { title: 'Accessibility', desc: 'Mint price (0.002 ETH) and on-chain storage mean anyone can participate.' },
              { title: 'Transparency', desc: 'Emission rates, decay curves, and multipliers are public constants.' }
            ].map((principle, i) => (
              <div key={i} className="bg-white/10 p-4 border-2 border-white/30">
                <h4 className="font-black text-green-400 mb-2">{principle.title}</h4>
                <p className="text-sm">{principle.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-yellow-400 text-black p-6 border-4 border-black mt-6">
            <p className="font-black text-xl mb-2">NO CRITICAL SECRETS</p>
            <p className="text-sm">NFT generation uses public blockchain data. No private keys control user assets after deployment.</p>
          </div>
        </div>
      </div>

      {/* Architecture Section */}
      <div id="architecture" className="bg-blue-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <div className="flex items-center gap-3 mb-6">
          <Code size={32} />
          <h2 className="text-3xl font-black uppercase font-mono">System Architecture</h2>
        </div>

        <div className="space-y-6">
          {/* Contract Grid */}
          <div className="grid md:grid-cols-3 gap-4">
            {[
              {
                name: 'DunkPoge NFT',
                type: 'ERC721A',
                features: ['10K supply', 'On-chain SVG', 'Batch minting', '5% royalty']
              },
              {
                name: 'Pogecoin (POGE)',
                type: 'ERC20',
                features: ['1B fixed supply', 'No admin', 'Staking rewards', 'Standard ERC20']
              },
              {
                name: 'Staking Contract',
                type: 'Vault',
                features: ['Immutable', 'Decay curves', '2x multipliers', 'Achievements']
              }
            ].map((contract, i) => (
              <div key={i} className="bg-white text-black p-6 border-4 border-black shadow-[6px_6px_0_0_#000]">
                <h3 className="font-black text-xl mb-2 uppercase">{contract.name}</h3>
                <p className="text-sm mb-4 text-blue-600 font-bold">{contract.type}</p>
                <ul className="space-y-2">
                  {contract.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm">
                      <span className="text-green-600 font-black">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Contract Addresses */}
          <div className="bg-black/30 p-6 border-2 border-white/50">
            <h3 className="font-black text-xl mb-4 text-yellow-300">CONTRACT ADDRESSES</h3>
            <div className="space-y-3">
              {contracts.map((contract, i) => (
                <div key={i} className="bg-white/10 p-4 border-2 border-white/20">
                  <div className="flex justify-between items-start gap-4 mb-3">
                    <div>
                      <div className="font-black text-sm text-green-400">{contract.name}</div>
                      <div className="text-xs opacity-75">{contract.network}</div>
                    </div>
                    <a
                      href={contract.explorer}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-blue-500 text-white px-3 py-1 border-2 border-white hover:bg-blue-600 transition-all text-xs font-black flex items-center gap-1 no-underline"
                    >
                      VIEW
                      <ExternalLink size={12} />
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-black/50 px-3 py-2 border border-white/30 font-mono break-all">
                      {contract.address}
                    </code>
                    <button
                      onClick={() => copyToClipboard(contract.address, `contract-${i}`)}
                      className="bg-green-400 text-black p-2 border-2 border-black hover:bg-green-300 transition-all flex-shrink-0"
                      title="Copy address"
                    >
                      {copiedAddress === `contract-${i}` ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* NFT Specifications */}
      <div className="bg-cyan-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <h2 className="text-3xl font-black uppercase font-mono mb-6">NFT Specifications</h2>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white text-black p-6 border-4 border-black">
            <h3 className="font-black text-xl mb-4 uppercase">Key Parameters</h3>
            <div className="space-y-3 font-mono text-sm">
              <div className="flex justify-between p-2 border-b-2 border-gray-300">
                <span>MAX SUPPLY:</span>
                <span className="font-black">10,000</span>
              </div>
              <div className="flex justify-between p-2 border-b-2 border-gray-300">
                <span>PRICE:</span>
                <span className="font-black">0.002 ETH</span>
              </div>
              <div className="flex justify-between p-2 border-b-2 border-gray-300">
                <span>MAX PER WALLET:</span>
                <span className="font-black">10</span>
              </div>
              <div className="flex justify-between p-2 border-b-2 border-gray-300">
                <span>ROYALTY:</span>
                <span className="font-black">5%</span>
              </div>
              <div className="flex justify-between p-2">
                <span>COMBINATIONS:</span>
                <span className="font-black text-purple-600">~2 BILLION</span>
              </div>
            </div>
          </div>

          <div className="bg-white text-black p-6 border-4 border-black">
            <h3 className="font-black text-xl mb-4 uppercase">Trait Layers</h3>
            <div className="space-y-2 font-mono text-sm">
              {[
                '10 skin tones',
                '7 eye colors',
                '7 lip colors',
                '18 hairstyles (80% styled)',
                '15 hair colors',
                '11 eyewear styles (60% styled)',
                '10 headwear styles (60% styled)',
                '8 accessory layer 1 (weighted)',
                '4 accessory layer 2 (85% none)',
                '5 accessory layer 3 (70% none)'
              ].map((trait, i) => (
                <div key={i} className="flex items-center gap-2 p-2 bg-gray-100 border border-gray-300">
                  <span className="text-blue-600 font-black">→</span>
                  {trait}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-yellow-400 text-black p-6 border-4 border-black mt-6">
          <h3 className="font-black text-xl mb-2">EMERGENT RARITY SYSTEM</h3>
          <p className="font-mono text-sm">
            Rarity is determined by actual mint distribution. Gaming the system makes targeted traits MORE COMMON, 
            creating natural manipulation resistance.
          </p>
        </div>
      </div>

      {/* Staking Mechanics */}
      <div className="bg-green-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <div className="flex items-center gap-3 mb-6">
          <Zap size={32} />
          <h2 className="text-3xl font-black uppercase font-mono">Staking Mechanics</h2>
        </div>

        <div className="space-y-6">
          {/* Key Parameters */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-black/30 p-6 border-2 border-white/50">
              <h3 className="font-black text-xl mb-4 text-yellow-300">EMISSION RATES</h3>
              <div className="space-y-2 text-sm font-mono">
                <div>BASE: ~1 POGE/day per NFT</div>
                <div>INITIAL BONUS: ~9 POGE/day per NFT</div>
                <div>DECAY PERIOD: 730 days</div>
                <div className="pt-2 border-t border-white/30">
                  <span className="font-black text-green-400">10 POGE/day → 1 POGE/day</span>
                </div>
              </div>
            </div>

            <div className="bg-black/30 p-6 border-2 border-white/50">
              <h3 className="font-black text-xl mb-4 text-yellow-300">LOYALTY MULTIPLIERS</h3>
              <div className="space-y-2 text-sm font-mono">
                <div>STARTING: 1.0x</div>
                <div>MAXIMUM: 2.0x</div>
                <div>GROWTH PERIOD: 180 days</div>
                <div className="pt-2 border-t border-white/30">
                  <span className="font-black text-green-400">LINEAR GROWTH</span>
                </div>
              </div>
            </div>
          </div>

          {/* Supply Planning */}
          <div className="bg-white text-black p-6 border-4 border-black">
            <h3 className="font-black text-xl mb-4 uppercase">Supply Planning</h3>
            <div className="space-y-3 font-mono text-sm">
              <div className="flex justify-between p-3 bg-gray-100 border-2 border-gray-300">
                <span>TOTAL POGE SUPPLY:</span>
                <span className="font-black text-purple-600">1,000,000,000</span>
              </div>
              <div className="flex justify-between p-3 bg-gray-100 border-2 border-gray-300">
                <span>WORST CASE USAGE:</span>
                <span className="font-black">~724M POGE</span>
              </div>
              <div className="flex justify-between p-3 bg-green-100 border-2 border-green-600">
                <span>SAFETY BUFFER:</span>
                <span className="font-black text-green-600">276M POGE (27.6%)</span>
              </div>
            </div>
          </div>

          {/* Achievements */}
          <div className="bg-black/30 p-6 border-2 border-white/50">
            <h3 className="font-black text-xl mb-4 text-yellow-300">ACHIEVEMENTS</h3>
            <div className="grid md:grid-cols-2 gap-3">
              {[
                { name: 'Early Adopter', req: 'Stake within first 30 days' },
                { name: 'Diamond Paws', req: 'Stake for 180+ days continuously' },
                { name: 'Collector', req: 'Own 10+ NFTs with 7+ days stake' },
                { name: 'Poge Whale', req: 'Earn 10,000+ POGE total' }
              ].map((achievement, i) => (
                <div key={i} className="bg-white/10 p-4 border-2 border-white/20">
                  <div className="font-black text-green-400 mb-1">{achievement.name}</div>
                  <div className="text-xs opacity-90">{achievement.req}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Security Section */}
      <div id="security" className="bg-red-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <div className="flex items-center gap-3 mb-6">
          <Lock size={32} />
          <h2 className="text-3xl font-black uppercase font-mono">Security & Trustlessness</h2>
        </div>

        <div className="space-y-6">
          {/* Guarantees */}
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'No External Dependencies', desc: 'NFT metadata entirely on-chain. No IPFS, no HTTP, no server dependencies.' },
              { title: 'User Control', desc: 'Your NFTs are always withdrawable. Pool depletion results in partial payment, not reverts.' },
              { title: 'Verifiability', desc: 'Anyone can verify rarity, pending rewards, and emission rates from contract code.' },
              { title: 'Censorship Resistance', desc: 'No admin can prevent unstaking or claiming rewards.' },
              { title: 'Immutability', desc: 'Staking contract has zero admin functions. Cannot be paused or upgraded.' },
              { title: 'Emergency Recovery', desc: 'emergencyWithdraw() always returns NFTs, even if rewards forfeit.' }
            ].map((guarantee, i) => (
              <div key={i} className="bg-white text-black p-6 border-4 border-black">
                <h3 className="font-black text-lg mb-2 text-red-600">{guarantee.title}</h3>
                <p className="text-sm font-mono">{guarantee.desc}</p>
              </div>
            ))}
          </div>

          {/* Audited Patterns */}
          <div className="bg-black/30 p-6 border-2 border-white/50">
            <h3 className="font-black text-xl mb-4 text-yellow-300">AUDITED PATTERNS</h3>
            <div className="grid md:grid-cols-2 gap-3 font-mono text-sm">
              {[
                'ReentrancyGuard on all state-changing functions',
                'SafeERC20 prevents silent transfer failures',
                'ERC721A battle-tested batch minting',
                'Checks-Effects-Interactions pattern',
                'No external calls except ERC20/721',
                'Stack depth optimized for gas efficiency'
              ].map((pattern, i) => (
                <div key={i} className="flex items-center gap-2 p-3 bg-white/10 border border-white/20">
                  <span className="text-green-400 font-black">✓</span>
                  {pattern}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Technical Details - Expandable */}
      <div className="bg-white text-black p-8 border-4 border-black shadow-[8px_8px_0_0_#000]">
        <button
          onClick={() => toggleSection('technical')}
          className="w-full flex items-center justify-between mb-6"
        >
          <h2 className="text-3xl font-black uppercase font-mono">Technical Deep Dive</h2>
          {expandedSections.technical ? <ChevronUp size={32} /> : <ChevronDown size={32} />}
        </button>

        {expandedSections.technical && (
          <div className="space-y-6">
            {/* SVG Generation */}
            <div className="bg-purple-100 p-6 border-4 border-purple-600">
              <h3 className="font-black text-xl mb-4 text-purple-600">ON-CHAIN SVG GENERATION</h3>
              <div className="space-y-3 font-mono text-sm">
                <p><strong>Why SVG?</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Resolution-independent (scale infinitely)</li>
                  <li>Smaller than base64 image data</li>
                  <li>Native browser support</li>
                  <li>Allows dynamic styling</li>
                </ul>
                <div className="bg-white p-4 border-2 border-purple-400 mt-4">
                  <code className="text-xs">
                    1. Retrieve seed for tokenId<br/>
                    2. Derive traits from seed (deterministic)<br/>
                    3. Build CSS with trait colors<br/>
                    4. Assemble SVG layers<br/>
                    5. Base64 encode SVG<br/>
                    6. Wrap in JSON metadata<br/>
                    7. Return as data URI
                  </code>
                </div>
              </div>
            </div>

            {/* Emission Math */}
            <div className="bg-blue-100 p-6 border-4 border-blue-600">
              <h3 className="font-black text-xl mb-4 text-blue-600">EMISSION DECAY MATHEMATICS</h3>
              <div className="space-y-3 font-mono text-sm">
                <p><strong>Quadratic Decay Formula:</strong></p>
                <div className="bg-white p-4 border-2 border-blue-400">
                  <code className="text-xs">
                    baseReward = duration × BASE_EMISSION<br/><br/>
                    bonusReward = INITIAL_BONUS × (t1² - t2²) / (2 × DECAY_PERIOD)<br/><br/>
                    finalReward = baseReward × loyaltyMultiplier
                  </code>
                </div>
                <p className="pt-3"><strong>Example Timeline:</strong></p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Day 1: ~10 POGE/day per NFT</li>
                  <li>Day 180: ~6.25 POGE/day (1.5x mult → 9.375 effective)</li>
                  <li>Day 365: ~3.75 POGE/day (2x mult → 7.5 effective)</li>
                  <li>Day 730: ~1 POGE/day (2x mult → 2 effective)</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Integration Guide */}
      <div className="bg-gradient-to-br from-purple-600 to-pink-600 text-white p-8 border-4 border-white shadow-[8px_8px_0_0_#fff]">
        <div className="flex items-center gap-3 mb-6">
          <Users size={32} />
          <h2 className="text-3xl font-black uppercase font-mono">Integration Guide</h2>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-black/30 p-6 border-2 border-white/50">
            <h3 className="font-black text-xl mb-4 text-yellow-300">READ OPERATIONS (FREE)</h3>
            <div className="space-y-2 font-mono text-xs">
              <div className="bg-white/10 p-3 border border-white/20">
                <code>nftContract.tokenURI(tokenId)</code>
              </div>
              <div className="bg-white/10 p-3 border border-white/20">
                <code>stakingContract.getStakeInfo(tokenId)</code>
              </div>
              <div className="bg-white/10 p-3 border border-white/20">
                <code>stakingContract.calculateRewards(tokenId)</code>
              </div>
              <div className="bg-white/10 p-3 border border-white/20">
                <code>stakingContract.getUserAchievements(address)</code>
              </div>
            </div>
          </div>

          <div className="bg-black/30 p-6 border-2 border-white/50">
            <h3 className="font-black text-xl mb-4 text-yellow-300">WRITE OPERATIONS (GAS)</h3>
            <div className="space-y-2 font-mono text-xs">
              <div className="bg-white/10 p-3 border border-white/20">
                <code>nftContract.mint(quantity, {'{ value }'})</code>
              </div>
              <div className="bg-white/10 p-3 border border-white/20">
                <code>nftContract.setApprovalForAll(staking, true)</code>
              </div>
              <div className="bg-white/10 p-3 border border-white/20">
                <code>stakingContract.stake([tokenIds])</code>
              </div>
              <div className="bg-white/10 p-3 border border-white/20">
                <code>stakingContract.claimRewards()</code>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Resources */}
      <div className="bg-black text-white p-8 border-4 border-green-400 shadow-[8px_8px_0_0_#00ff00]">
        <h2 className="text-3xl font-black uppercase font-mono mb-6 text-center">Resources</h2>
        <div className="grid md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {[
            { label: 'GitHub', icon: Github, url: 'https://github.com/dunkpoge' },
            { label: 'Twitter', icon: Twitter, url: 'https://twitter.com/dunkpoge' },
            { label: 'Discord', icon: MessageCircle, url: 'https://discord.gg/7PsZwC3TZX' },
            { label: 'Manifesto', icon: Book, url: 'https://trustlessness.eth.limo/general/2025/11/11/the-trustless-manifesto.html' }
          ].map((resource) => {
            const Icon = resource.icon;
            return (
              <a
                key={resource.label}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-black font-black py-4 px-6 border-4 border-white hover:bg-gray-200 transition-all flex flex-col items-center gap-2 no-underline"
              >
                <Icon size={24} />
                <span className="text-sm">{resource.label}</span>
              </a>
            );
          })}
        </div>
      </div>

      {/* Footer Quote */}
      <div className="text-center">
        <div className="inline-block bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white px-8 py-6 border-4 border-black shadow-[8px_8px_0_0_#000]">
          <p className="text-2xl font-black font-mono italic mb-2">
            "Life's a whole carousel-colored Ponzi scheme"
          </p>
          <p className="text-sm opacity-90">— The Song, Verse IV</p>
        </div>
      </div>

      {/* Bottom */}
      <div className="text-center">
        <div className="inline-block bg-green-400 text-black px-8 py-4 border-4 border-black shadow-[8px_8px_0_0_#000]">
          <p className="text-xl font-black font-mono uppercase">
            Much Documentation • Very Trustless • Wow
          </p>
        </div>
      </div>
    </div>
  );
};